import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Paper,
  Button,
  Stack,
  Alert,
  AlertTitle,
  LinearProgress,
  Chip,
  Divider
} from '@mui/material'
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Refresh as ResetIcon,
  Check as CheckIcon
} from '@mui/icons-material'
import { ProcessingSession, ExtractedQuestion, ProgressInfo, StudyContext, SurveyTextBlock } from '../types'
import { config } from '../config/environment'
import { useUserIdentityContext } from '../contexts/UserIdentityContext'
import { usePostHog } from '../contexts/PostHogProvider'
import FeatureStatusAlert from './FeatureStatusAlert'
import { useFeatureStatus } from '../hooks/useFeatureStatus'
import { AIBadge } from './AIBadge'
import TabPlanDocumentUploadStep, { TabPlanDocumentUploadRef } from './steps/TabPlanDocumentUploadStep'
import QuestionReviewStep from './steps/QuestionReviewStep'
import TabPlanGenerationStep from './steps/TabPlanGenerationStep'
import StudyContextSection from './StudyContextSection'
import TextBlocksSection from './TextBlocksSection'

interface TabPlanWizardProps {
  onComplete?: (session: ProcessingSession) => void
  onError?: (error: string) => void
  onProgressUpdate?: (progress: ProgressInfo) => void
}

const steps = [
  'Document Processing',
  'Review Questions', 
  'Generate Tab Plan'
]

export const TabPlanWizard: React.FC<TabPlanWizardProps> = ({
  onComplete,
  onError,
  onProgressUpdate
}) => {
  // Read modelId from URL query parameter (backend will resolve default for tab-plan-generator)
  const urlParams = new URLSearchParams(window.location.search)
  const modelId = urlParams.get('modelId') || undefined
  const featureStatus = useFeatureStatus()
  const {email: userEmail, department} = useUserIdentityContext()
  const {trackEvent, identifyUser} = usePostHog()
  const tabPlanCompletedRef = useRef(false)
  const activeStepRef = useRef(0)
  const hasStartedExtractionRef = useRef(false)
  useEffect(() => {
    if (userEmail) {
      identifyUser(userEmail, {department: department || undefined})
    }
  }, [userEmail, department])

  useEffect(() => {
    const trackAbandonment = () => {
      if ((activeStepRef.current > 0 || hasStartedExtractionRef.current) && !tabPlanCompletedRef.current) {
        trackEvent('tab_plan_abandoned', {
          user_department: department,
        })
      }
    }
    window.addEventListener('beforeunload', trackAbandonment)
    return () => {
      window.removeEventListener('beforeunload', trackAbandonment)
      trackAbandonment()
    }
  }, [])

  const [activeStep, setActiveStep] = useState(0)
  useEffect(() => { activeStepRef.current = activeStep }, [activeStep])
  const [session, setSession] = useState<ProcessingSession | null>(null)
  const [extractedQuestions, setExtractedQuestions] = useState<ExtractedQuestion[]>([])
  const [studyContext, setStudyContext] = useState<StudyContext[]>([])
  const [textBlocks, setTextBlocks] = useState<SurveyTextBlock[]>([])
  const [sectionOrder, setSectionOrder] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState<ProgressInfo | null>(null)
  const [eventSource, setEventSource] = useState<EventSource | null>(null)
  const [hasFileSelected, setHasFileSelected] = useState(false)
  const [hasStartedExtraction, setHasStartedExtraction] = useState(false)
  useEffect(() => { hasStartedExtractionRef.current = hasStartedExtraction }, [hasStartedExtraction])
  const [phase1StartTime, setPhase1StartTime] = useState<number | null>(null)
  const [hasHandledCompletion, setHasHandledCompletion] = useState(false)
  const uploadStepRef = useRef<TabPlanDocumentUploadRef>(null)

  // Connect to SSE when session is created
  useEffect(() => {
    if (session?.sessionId && !eventSource) {
      const sse = new EventSource(`${config.API_BASE_URL}/api/tabplan/progress/${session.sessionId}`, { withCredentials: true })
      
      sse.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          
          if (data.type === 'connection') {
            return
          }
          
          // Handle Phase 1 progress (question ID extraction) with artificial delays
          if (data.type === 'question-extraction-phase1') {
            // Track when Phase 1 starts (use actual real time, not delayed time)
            if (!phase1StartTime) {
              setPhase1StartTime(Date.now())
            }
            
            const phaseMessage = data.message || `Phase 1: Extracting question IDs (${data.current || 0}/${data.total || 2})`
            const progressUpdate = {
              ...data,
              message: phaseMessage
            }
            
            
            // Create artificial delays based on the SSE event content
            const delayTime = data.message?.includes('complete') ? 3000 : 2000 // Phase 1 complete gets 3s, start gets 2s
            
            setTimeout(() => {
              setProgress(progressUpdate)
              onProgressUpdate?.(progressUpdate)
            }, delayTime)
          }
          
          // Handle Phase 2 progress (question details extraction)
          if (data.type === 'question-extraction-phase2') {
            const phase2Message = data.message || `Phase 2: Extracting question details (${data.current || 0}/${data.total || 0})`
            const phase2Progress = {
              ...data,
              message: phase2Message
            }
            
            // For Phase 2 start event, add artificial delay to create proper sequence
            if (data.message?.includes('Starting Phase 2') || (data.current === 0 && data.total > 0)) {
              setTimeout(() => {
                setProgress(phase2Progress)
                onProgressUpdate?.(phase2Progress)
                
                // Reset Phase 1 tracking after Phase 2 starts
                setPhase1StartTime(null)
                  }, 6000) // Show Phase 2 start after Phase 1 events have had time to display
            } else {
              // Real-time progress updates during actual Phase 2 processing
              setProgress(phase2Progress)
              onProgressUpdate?.(phase2Progress)
            }
          }
          
          // Handle completion
          if (data.type === 'question-extraction-complete' && data.questions && !hasHandledCompletion) {
            setHasHandledCompletion(true)
            setExtractedQuestions(data.questions)
            setStudyContext(data.studyContext || [])
            setTextBlocks(data.textBlocks || [])
            setSectionOrder(data.sectionOrder || [])
            
            // Update session with merge data from SSE
            if (session) {
              const updatedSession = {
                ...session,
                warnings: data.warnings,
                mergeStats: data.mergeStats
              }
              setSession(updatedSession)
            }
            setError(null)
            const completionProgress = {
              type: 'completion',
              percentage: 100,
              message: `Successfully extracted ${data.questions.length} questions!`
            }
            setProgress(completionProgress)
            onProgressUpdate?.(completionProgress)
            
            // Reset Phase 1 tracking
            setPhase1StartTime(null)
            
            // Close the SSE connection immediately since processing is complete
            sse.close()
            setEventSource(null)
            
            // Auto-advance from Step 0 (Document Processing) to Step 1 (Review Questions)
            setTimeout(() => {
              handleNext()
              setProgress(null)
            }, 1000)
          }
          
          // Handle other event types (connection, etc.) without specific progress updates
          if (data.type === 'connection') {
            // Connection events don't need progress updates
            return
          }
          
          // Handle errors
          if (data.type === 'error') {
            setError(data.message || 'Processing failed')
            setProgress(null)
            setHasStartedExtraction(false)
            setPhase1StartTime(null)
            trackEvent('tab_plan_error', {
              error_message: data.message || 'Processing failed',
              error_type: 'extraction_failed',
              user_department: department,
            })
            sse.close()
            setEventSource(null)
          }
          
        } catch (error) {
          console.error('Error parsing SSE message:', error)
        }
      }
      
      sse.onerror = (error) => {
        console.error('SSE connection error:', error)
        setError('Connection error occurred')
        trackEvent('tab_plan_error', {
          error_message: 'Connection error occurred',
          error_type: 'connection_error',
          user_department: department,
        })
      }
      
      setEventSource(sse)
    }
    
    return () => {
      if (eventSource) {
        eventSource.close()
        setEventSource(null)
      }
    }
  }, [session?.sessionId, onProgressUpdate])

  // Close SSE connection when we successfully reach Step 1 (Review Questions) with questions loaded
  useEffect(() => {
    if (activeStep === 1 && extractedQuestions.length > 0 && eventSource) {
      eventSource.close()
      setEventSource(null)
    }
  }, [activeStep, extractedQuestions.length, eventSource])

  const handleNext = () => {
    setActiveStep((prevActiveStep) => {
      const nextStep = Math.min(prevActiveStep + 1, steps.length - 1)
      return nextStep
    })
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleReset = () => {
    // Close SSE connection
    if (eventSource) {
      eventSource.close()
      setEventSource(null)
    }
    
    setActiveStep(0)
    setSession(null)
    setExtractedQuestions([])
    setStudyContext([])
    setTextBlocks([])
    setSectionOrder([])
    setError(null)
    setIsLoading(false)
    setProgress(null)
    setHasFileSelected(false)
    setHasStartedExtraction(false)
    setPhase1StartTime(null)
    setHasHandledCompletion(false)
  }

  const handleError = (errorMessage: string) => {
    setError(errorMessage)
    setProgress(null)
    setHasStartedExtraction(false)
    setPhase1StartTime(null)
    trackEvent('tab_plan_error', {
      error_message: errorMessage,
      error_type: 'component_error',
      user_department: department,
    })
    onError?.(errorMessage)
  }

  const handleProgressUpdate = (progressInfo: ProgressInfo) => {
    setProgress(progressInfo)
    onProgressUpdate?.(progressInfo)
  }

  const handleUploadComplete = (newSession: ProcessingSession) => {
    setSession(newSession)
    setError(null)
    setProgress({
      type: 'completion',
      percentage: 100,
      message: 'Document uploaded successfully'
    })
    
    // SSE will handle question extraction progress - no need for frontend-generated message
    // Don't auto-advance here - let SSE completion handler advance to Step 2
    // The upload is just the first part of Step 1, question extraction still needs to complete
  }

  const handleQuestionsUpdated = (questions: ExtractedQuestion[]) => {
    setExtractedQuestions(questions)
    setError(null)
  }

  const handleFileSelected = (hasFile: boolean) => {
    setHasFileSelected(hasFile)
  }

  const canProceedToNext = () => {
    switch (activeStep) {
      case 0:
        return hasFileSelected && !hasStartedExtraction // Can start extraction when file selected and not started yet
      case 1:
        return extractedQuestions.length > 0 // Can proceed to generate when questions exist  
      case 2:
        return false // Final step
      default:
        return false
    }
  }

  const handleNextClick = () => {
    if (activeStep === 0) {
      // Trigger upload and extraction, stay on Step 1
      setHasStartedExtraction(true)
      uploadStepRef.current?.triggerUploadAndExtraction()
      // Stay on Step 1 - SSE completion will auto-advance to Step 2
    } else {
      // Normal next step for other steps (Step 1 → Step 2 handled by SSE)
      handleNext()
    }
  }

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <TabPlanDocumentUploadStep
            ref={uploadStepRef}
            onUploadComplete={handleUploadComplete}
            onError={handleError}
            onProgressUpdate={handleProgressUpdate}
            onFileSelected={handleFileSelected}
            isLoading={isLoading}
            autoExtract={true}
            modelId={modelId}
          />
        )
      case 1:
        return session && extractedQuestions.length > 0 ? (
          <Box>
            <StudyContextSection studyContext={studyContext} />
            <QuestionReviewStep
              sessionId={session.sessionId}
              questions={extractedQuestions}
              sectionOrder={sectionOrder}
              onQuestionsUpdated={handleQuestionsUpdated}
              onError={handleError}
              mergeWarnings={(session as any).warnings || []}
              mergeStats={(session as any).mergeStats}
            />
            <TextBlocksSection 
              textBlocks={textBlocks}
              onTextBlocksUpdated={(updatedBlocks: SurveyTextBlock[]) => {
                setTextBlocks(updatedBlocks)
              }}
            />
          </Box>
        ) : (
          <Alert severity="error">
            No questions found. Please go back and process a document first.
          </Alert>
        )
      case 2:
        return session && extractedQuestions.length > 0 ? (
          <TabPlanGenerationStep
            sessionId={session.sessionId}
            questions={extractedQuestions}
            onError={handleError}
            onGenerationComplete={() => {
              tabPlanCompletedRef.current = true
              trackEvent('tab_plan_completed', {
                user_department: department,
              })
            }}
          />
        ) : (
          <Alert severity="error">
            No questions found. Please go back and process questions first.
          </Alert>
        )
      default:
        return <Typography>Unknown step</Typography>
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Tab Plan Generator
        </Typography>
        <AIBadge
          tooltip="AI-powered tab plan generation: Upload survey documents to automatically extract questions and generate structured Excel tab plans"
          variant="filled"
        />
      </Box>
      
      <Typography color="text.secondary" paragraph>
        Convert your survey documents into structured tab plans for data analysis. 
        Upload a Word document, extract questions with AI, review and edit, then generate Excel output.
      </Typography>

      <FeatureStatusAlert status={featureStatus || undefined} />

      <Paper sx={{ p: 3, mb: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Real-time Progress Display */}
        {progress && (
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {progress.message}
              </Typography>
              {progress.percentage !== undefined && (
                <Chip 
                  label={`${progress.percentage}%`} 
                  size="small" 
                  color="primary" 
                />
              )}
            </Stack>
            {progress.percentage !== undefined && (
              <LinearProgress 
                variant="determinate" 
                value={progress.percentage} 
                sx={{ height: 6, borderRadius: 3 }}
              />
            )}
          </Box>
        )}

        <Box sx={{ mb: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          )}

          {renderStepContent(activeStep)}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={handleBack}
            disabled={activeStep === 0}
          >
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {session && (
              <Typography variant="body2" color="text.secondary">
                Session: {session.sessionId.slice(0, 8)}...
              </Typography>
            )}
            {extractedQuestions.length > 0 && (
              <Chip 
                label={`${extractedQuestions.length} questions`} 
                size="small" 
                color="primary"
                variant="outlined"
              />
            )}
            
            <Button
              startIcon={<ResetIcon />}
              onClick={handleReset}
              variant="outlined"
            >
              Start Over
            </Button>

            {activeStep < steps.length - 1 && (
              <Button
                endIcon={<NextIcon />}
                onClick={handleNextClick}
                variant="contained"
                disabled={!canProceedToNext()}
              >
                {activeStep === 0
                  ? hasStartedExtraction
                    ? 'Processing...'
                    : 'Process Document'
                  : 'Next'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}

export default TabPlanWizard